import React, { useCallback, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi, normalizeList } from '../../../lib/admin-api/index.js';
import { useSupportTicketsList } from '../hooks/use-support-tickets-list.js';
import SupportTicketsPageView from './support-tickets-page-view.jsx';

function pickFirst(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function mapAdminProfileOptions(payload) {
  return normalizeList(payload)
    .map((profile, index) => {
      const id = String(pickFirst(profile?.uid, profile?.id) || '').trim();
      if (!id) return null;
      const firstName = pickFirst(profile?.firstName, profile?.first_name);
      const lastName = pickFirst(profile?.lastName, profile?.last_name);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      const fallback = pickFirst(profile?.name, profile?.email, `Profile ${index + 1}`);
      return { id, label: fullName || String(fallback || '').trim() || id };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label));
}

function SupportTicketsPageContainer() {
  const { token } = useAuth();
  const state = useSupportTicketsList();

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignTargetUid, setAssignTargetUid] = useState('');
  const [assignTargetName, setAssignTargetName] = useState('');
  const [adminProfileOptions, setAdminProfileOptions] = useState([]);
  const [isAdminProfilesLoading, setIsAdminProfilesLoading] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState('');
  const [isAssignmentSaving, setIsAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');

  const loadAdminProfiles = useCallback(async () => {
    setIsAdminProfilesLoading(true);
    try {
      const response = await adminApi.getProfiles(token, { isAdmin: true, limit: 100 });
      setAdminProfileOptions(mapAdminProfileOptions(response));
    } catch {
      setAdminProfileOptions([]);
    } finally {
      setIsAdminProfilesLoading(false);
    }
  }, [token]);

  const openAssignModal = async (row) => {
    setAssignTargetUid(String(row?.uid || '').trim());
    setAssignTargetName(String(row?.profileName || '').trim() || '-');
    setSelectedManagerId(String(row?.assignedId || '').trim());
    setAssignmentError('');
    setIsAssignModalOpen(true);
    await loadAdminProfiles();
  };

  const closeAssignModal = () => {
    if (isAssignmentSaving) return;
    setIsAssignModalOpen(false);
    setAssignTargetUid('');
    setAssignTargetName('');
    setSelectedManagerId('');
    setAssignmentError('');
  };

  const assignManager = async () => {
    const managerId = String(selectedManagerId || '').trim();
    if (!managerId) {
      setAssignmentError('Select a manager to assign');
      return;
    }

    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.assignSupportTicket(token, assignTargetUid, managerId);
      closeAssignModal();
      await state.reload();
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to assign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  const unassignManager = async () => {
    setIsAssignmentSaving(true);
    setAssignmentError('');

    try {
      await adminApi.unassignSupportTicket(token, assignTargetUid);
      closeAssignModal();
      await state.reload();
    } catch (requestError) {
      setAssignmentError(requestError.message || 'Failed to unassign manager');
    } finally {
      setIsAssignmentSaving(false);
    }
  };

  return (
    <SupportTicketsPageView
      {...state}
      isAssignModalOpen={isAssignModalOpen}
      adminProfileOptions={adminProfileOptions}
      isAdminProfilesLoading={isAdminProfilesLoading}
      selectedManagerId={selectedManagerId}
      isAssignmentSaving={isAssignmentSaving}
      assignmentError={assignmentError}
      assignTargetName={assignTargetName}
      openAssignModal={openAssignModal}
      closeAssignModal={closeAssignModal}
      onSelectManager={(id) => setSelectedManagerId(String(id || '').trim())}
      assignManager={assignManager}
      unassignManager={unassignManager}
    />
  );
}

export default SupportTicketsPageContainer;
